import React from 'react';
import { Route, Routes, useNavigate, Outlet } from 'react-router-dom';
import { Steps } from 'antd';

import requireAuth from '../components/hocs/requireAuth';
import AdminTemplate from '../layout/AdminTemplate';
import { UserRole } from '../features/PrerenderUser';
import { useHasCompletedSurvey, usePrerenderUserId } from '../hooks/usePrerenderUser';
import { useAnswersShortSurvey } from '~features/OnboardingSurvey';
import FullPageHeightContainer from '../layout/FullPageHeightContainer';
import {
  useWizardSteps,
  WizardSteps,
  CodeToPath,
  FULL_WIZARD_STEPS,
  StepCodes,
  WizardColumn,
  WizardGutterProvider,
} from '../features/integrationWizard';
import { SetupChecklistContainer as SetupChecklist } from '../features/SetupChecklist';
import { useEvent } from '../features/events/hooks/useEvent';
import { type WizardStep } from '~features/integrationWizard/steps';

const META_TAGS = {
  title: 'Get Started - Prerender.io',
};

const FIRST_STEP_WITHOUT_SIDEBAR = FULL_WIZARD_STEPS.findIndex((step) => step.code === StepCodes.Integrate);

function IntegrationWizardStepsLayout({
  stepsConfig,
  currentStepIndex,
}: {
  stepsConfig: WizardStep[];
  currentStepIndex: number;
}) {
  const { track } = useEvent();

  React.useEffect(() => {
    track('Integration Viewed');
  }, []);

  return (
    <WizardGutterProvider>
      <WizardColumn>
        <Steps current={currentStepIndex} items={stepsConfig} style={{ marginBottom: 24 }} />
        <Outlet />
      </WizardColumn>
    </WizardGutterProvider>
  );
}

function IntegrationWizardPage() {
  const userId = usePrerenderUserId();
  const hasCompletedSurvey = useHasCompletedSurvey();
  const answersShortSurvey = useAnswersShortSurvey();
  const { track } = useEvent();
  const navigate = useNavigate();

  const { stepsConfig, currentStepIndex, goNext, goBack, jumpToStep } = useWizardSteps(userId, hasCompletedSurvey);

  const onUpgrade = () => {
    track('Integration wizard: Upgrade now button clicked');
    navigate('/billing/plans');
  };

  return (
    <AdminTemplate metaTags={META_TAGS} hasSidebar={currentStepIndex < FIRST_STEP_WITHOUT_SIDEBAR}>
      <FullPageHeightContainer>
        <Routes>
          <Route
            path="steps"
            element={<IntegrationWizardStepsLayout stepsConfig={stepsConfig} currentStepIndex={currentStepIndex} />}
          >
            <Route
              path={CodeToPath[StepCodes.Personalisation]}
              element={<WizardSteps.Personalisation onGoNext={goNext} isShortSurvey={answersShortSurvey} />}
            />
            <Route
              path={CodeToPath[StepCodes.InviteTeamMember]}
              element={<WizardSteps.InviteTeam onGoNext={goNext} />}
            />

            <Route
              path={CodeToPath[StepCodes.Integrate]}
              element={<WizardSteps.Integrate onGoNext={goNext} onGoBack={goBack} jumpToStep={jumpToStep} />}
            />

            <Route
              path={`${CodeToPath[StepCodes.VerifyIntegration]}/*`}
              element={<WizardSteps.VerifyIntegration onGoNext={goNext} onGoBack={goBack} jumpToStep={jumpToStep} />}
            />

            <Route
              path={CodeToPath[StepCodes.StartRendering]}
              element={<WizardSteps.StartRendering onUpgrade={onUpgrade} />}
            />
          </Route>

          <Route path="setup-checklist" element={<SetupChecklist />} />
        </Routes>
      </FullPageHeightContainer>
    </AdminTemplate>
  );
}

export default requireAuth(IntegrationWizardPage, ({ role }) => role !== UserRole.Guest);
